/**
 * TORQADORN Customer Care widget (section).
 * Main view: FAQ links (tabs) + Contact. Detail view: slides in from right with back + tab title + 6 accordions.
 * Uses theme dialog.js for modal open/close. Trigger icon via CSS :has(dialog[open]).
 */

class CustomerCareWidget extends HTMLElement {
  constructor() {
    super();
    this._onDialogClose = this._onDialogClose.bind(this);
    this._onTabClick = this._onTabClick.bind(this);
    this._onBackClick = this._onBackClick.bind(this);
  }

  connectedCallback() {
    const dialogWrapper = this.querySelector('dialog-component');
    if (dialogWrapper) {
      dialogWrapper.addEventListener('dialog:close', this._onDialogClose);
    }
    this.querySelectorAll('.customer-care-widget__faq-link').forEach((link) => {
      link.addEventListener('click', this._onTabClick);
    });
    const backBtn = this.querySelector('[data-back]');
    if (backBtn) backBtn.addEventListener('click', this._onBackClick);
    this._updateCallAvailability();
  }

  disconnectedCallback() {
    const dialogWrapper = this.querySelector('dialog-component');
    if (dialogWrapper) dialogWrapper.removeEventListener('dialog:close', this._onDialogClose);
    this.querySelectorAll('.customer-care-widget__faq-link').forEach((link) => {
      link.removeEventListener('click', this._onTabClick);
    });
    const backBtn = this.querySelector('[data-back]');
    if (backBtn) backBtn.removeEventListener('click', this._onBackClick);
  }

  _onDialogClose() {
    const mainView = this.querySelector('[data-view="main"]');
    const detailView = this.querySelector('[data-view="detail"]');
    const backBtn = this.querySelector('[data-back]');
    const headerTitle = this.querySelector('.customer-care-widget__title');
    const mainTitle = this.getAttribute('data-header-title') || 'TORQADORN Customer Care';
    this._finishShowMainView(mainView, detailView, backBtn, headerTitle, mainTitle);
  }

  _showMainView() {
    const mainView = this.querySelector('[data-view="main"]');
    const detailView = this.querySelector('[data-view="detail"]');
    const backBtn = this.querySelector('[data-back]');
    const headerTitle = this.querySelector('.customer-care-widget__title');
    const mainTitle = this.getAttribute('data-header-title') || 'TORQADORN Customer Care';
    const DURATION_MS = 250;

    if (!detailView || !detailView.classList.contains('is-open')) {
      this._finishShowMainView(mainView, detailView, backBtn, headerTitle, mainTitle);
      return;
    }

    /* Play slide-out: show main behind, then detail slides right (250ms), then hide detail */
    if (mainView) mainView.hidden = false;
    if (backBtn) backBtn.hidden = true;
    if (headerTitle) headerTitle.textContent = mainTitle;
    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      panel.hidden = true;
    });
    detailView.classList.remove('is-open');

    const onDone = () => {
      detailView.removeEventListener('transitionend', onDone);
      if (detailView && !detailView.classList.contains('is-open')) detailView.hidden = true;
    };
    detailView.addEventListener('transitionend', onDone);
    setTimeout(() => {
      detailView.removeEventListener('transitionend', onDone);
      if (detailView && !detailView.classList.contains('is-open')) detailView.hidden = true;
    }, DURATION_MS);
  }

  _finishShowMainView(mainView, detailView, backBtn, headerTitle, mainTitle) {
    if (mainView) mainView.hidden = false;
    if (detailView) detailView.hidden = true;
    detailView?.classList.remove('is-open');
    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      panel.hidden = true;
    });
    if (backBtn) backBtn.hidden = true;
    if (headerTitle) headerTitle.textContent = mainTitle || 'TORQADORN Customer Care';
  }

  _showDetailView(tabId) {
    const mainView = this.querySelector('[data-view="main"]');
    const detailView = this.querySelector('[data-view="detail"]');
    const backBtn = this.querySelector('[data-back]');
    const headerTitle = this.querySelector('.customer-care-widget__title');
    const tabTitle = this.getAttribute(`data-tab-${tabId}-title`) || `Tab ${tabId}`;
    const panel = this.querySelector(`[data-tab-panel="${tabId}"]`);
    if (mainView) mainView.hidden = true;
    if (detailView) {
      detailView.hidden = false;
      requestAnimationFrame(() => detailView.classList.add('is-open'));
    }
    this.querySelectorAll('[data-tab-panel]').forEach((p) => {
      p.hidden = true;
    });
    if (panel) panel.hidden = false;
    if (backBtn) backBtn.hidden = false;
    if (headerTitle) headerTitle.textContent = tabTitle;
  }

  _onTabClick(e) {
    e.preventDefault();
    const link = /** @type {HTMLElement} */ (e.currentTarget);
    const tab = link.getAttribute('data-tab');
    if (tab) this._showDetailView(tab);
  }

  _onBackClick(e) {
    e.preventDefault();
    this._showMainView();
  }

  _updateCallAvailability() {
    const callBtn = this.querySelector('[data-contact="call"]');
    if (!callBtn) return;
    const now = new Date();
    // Get UTC hour and minute, convert to IST (UTC+5:30), handle hour and minute wrapping
    let utcHour = now.getUTCHours();
    let utcMinute = now.getUTCMinutes();

    let istHour = utcHour + 5;
    let istMinute = utcMinute + 30;

    if (istMinute >= 60) {
      istMinute -= 60;
      istHour += 1;
    }
    if (istHour >= 24) { 
      istHour -= 24;
    }

    const istHours = parseFloat(istHour + "." + istMinute.toString().padStart(2, '0'));
    const endHours = 18.00;
    const isAvailable = istHours >= 10 && istHours < endHours;

    callBtn.classList.toggle('customer-care-widget__contact-btn--unavailable', !isAvailable);
    if (!isAvailable) {
      callBtn.setAttribute('aria-disabled', 'true');
      callBtn.removeAttribute('href');
      callBtn.href = '#';
    } else {
      callBtn.removeAttribute('aria-disabled');
      const phone = this.getAttribute('data-phone');
      if (phone) callBtn.href = `tel:${phone}`;
    }
  }
}

if (!customElements.get('customer-care-widget')) {
  customElements.define('customer-care-widget', CustomerCareWidget);
}
